import { CheckCircleOutlined } from "@ant-design/icons";
import { Alert as AntAlert, Card } from "antd";
import { useTranslation } from "react-i18next";
import { useActiveErrors } from "../../../api";
import { Alert } from "../../../api/models";
import "../../../styles/card.scss";
import { BaseDrawer } from "../../BaseDrawer";
import { ActiveErrorsLabel } from "../../BeaconActiveErrorsTooltip";
import { BeaconErrorStatus } from "../../BeaconErrorStatus";
import { AlertTable } from "./AlertTable";
import { PagedApiState } from "../../../api/useApiState";
import { formatLocalizedDateTime } from "../../../util/formatting";

interface BeaconAlertDrawerProps {
  serial?: string;
  alertHistory:  PagedApiState<Alert>;
  alertDrawerVisible: boolean;
  isLoading: boolean;
  onCloseDrawer: (visible: boolean) => void;
}

export function BeaconAlertDrawer({
  serial = "",
  alertHistory,
  alertDrawerVisible,

  isLoading,
  onCloseDrawer,
}: BeaconAlertDrawerProps) {
  const { t } = useTranslation();

  const activeAlerts = useActiveErrors(serial);

  return (
    <BaseDrawer
      title={t("beacon:error-details")}
      drawerVisible={alertDrawerVisible}
      onCloseDrawer={onCloseDrawer}
      dataTestId="beacon-alert-drawer"
    >
      <ActiveAlertsCard activeAlerts={activeAlerts?.value || []} />
      <AlertTable isLoading={isLoading} alertHistory={alertHistory} />
    </BaseDrawer>
  );
}

interface SCBOneAlertDrawerProps {
  alertHistory: PagedApiState<Alert> | undefined;
  alertDrawerVisible: boolean;
  operationRunSelected?: boolean;
  isLoading: boolean;
  onCloseDrawer: (visible: boolean) => void;
}

export function SCBOneAlertDrawer({
  alertHistory,
  alertDrawerVisible,
  operationRunSelected,
  isLoading,
  onCloseDrawer,
}: SCBOneAlertDrawerProps) {
  const { t } = useTranslation();

  return (
    <BaseDrawer
      title={t("beacon:error-details")}
      drawerVisible={alertDrawerVisible}
      onCloseDrawer={onCloseDrawer}
      dataTestId="scb-alert-drawer"
    >
      {operationRunSelected && alertHistory ? (
        <AlertTable alertHistory={alertHistory} isLoading={isLoading} />
      ) : (
        <AntAlert
          message={t("scb-one:run-notification-select")}
          type="info"
          showIcon
        />
      )}
    </BaseDrawer>
  );
}

interface AlertCardProps {
  activeAlerts: Alert[];
}

function ActiveAlertsCard({ activeAlerts }: AlertCardProps) {
  const { t } = useTranslation();

  const activeAlertsCount = activeAlerts.length;

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "auto 1fr",
        gridColumnGap: "1em",
        gridRowGap: "1em",
        paddingBottom: "1em",
      }}
    >
      <Card style={{ display: "flex", alignItems: "center" }}>
        {activeAlertsCount === 0 ? (
          <div style={{ textAlign: "center" }}>
            <CheckCircleOutlined style={{ color: "green", fontSize: "3em" }} />
            <div>{t("error:no-active-errors")}</div>
          </div>
        ) : (
          <div style={{ textAlign: "center" }}>
            <BeaconErrorStatus errors={activeAlerts} showTooltip={true} />
            <div>
              <ActiveErrorsLabel errors={activeAlerts} />
            </div>
          </div>
        )}
      </Card>
      {activeAlertsCount > 0 && (
        <Card style={{ display: "flex", alignItems: "center" }}>
          <div>
            {activeAlerts!.map((e, i) => (
              <div key={i}>
                <span style={{ fontWeight: "bold" }}>{e.alert} : </span>
                <span>
                  {t("error:active-since", {
                    since: formatLocalizedDateTime(e.ts),
                  })}
                </span>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
